// Shared status vocabulary for the AI manual-crawl run (task device_manual_crawl,
// ADR 0073/0076). Both crawl surfaces poll the same audit-exempt
// GET /v1/admin/manuals/crawl-runs endpoint and need to agree on its shape, the
// poll cadence, and when a still-'running' row has become a dead-worker zombie:
//
//   - AdminManuals       — the cross-model Review page tracks the LATEST run
//   - AdminDeviceDetail   — the per-model device page tracks the ONE run it just
//                           started (by id) so "Find manuals (AI)" shows live
//                           "Scanning…" and a completion status
//
// Keeping these here means a change to the poll interval or staleness threshold
// lands in one place rather than drifting between the two pages.

export interface CrawlRun {
  id: string;
  status: 'running' | 'succeeded' | 'failed';
  openrouter_model: string | null;
  models_scanned: number;
  manuals_suggested: number;
  error_count: number;
  error: string | null;
  started_at: string;
  finished_at: string | null;
  // Estimated USD this run spent on web search, and the billable query count
  // behind it. null for a run from before the search_usage ledger landed.
  search_cost_usd: number | null;
  search_queries: number | null;
}

// One recorded error behind a run's `error_count`, served by
// GET /v1/admin/manuals/crawl-runs/:id/errors and rendered by the Errors
// drill-down (CrawlRunErrors). `kind` is the stage that failed; `device_model_id`
// links to the model's admin page when set; `http_status` is the provider status
// when the failure carried one. Mirrors the backend serializeCrawlRunError shape.
export interface CrawlRunError {
  id: string;
  kind: 'search' | 'fetch' | 'model' | string;
  message: string;
  http_status: number | null;
  url: string | null;
  device_model_id: string | null;
  model_label: string | null;
  created_at: string;
}

// How often a crawl surface re-checks a running run's status. Modest for a tool
// a handful of operators use; the crawl-runs read is audit-exempt so the cadence
// costs nothing in the audit log.
export const CRAWL_POLL_MS = 5000;

// A run still 'running' longer than this is treated as a dead worker — a crawl
// whose process was killed mid-run (deploy/OOM) never writes its terminal
// status. Past it a page stops polling AND stops treating the run as active, so
// the trigger re-enables and the operator can start a fresh crawl. Generous so a
// real long fleet sweep isn't cut off.
const CRAWL_STALE_MS = 60 * 60 * 1000;

// True when a 'running' crawl_runs row is old enough to be a zombie. Recompute at
// render (and per poll tick) so it flips as wall-clock time passes.
export function isRunStale(startedAt: string | null): boolean {
  if (startedAt === null) return false;
  const ms = Date.parse(startedAt);
  return Number.isFinite(ms) && Date.now() - ms > CRAWL_STALE_MS;
}
